import { useEffect, useRef, useState, useCallback } from 'react'
import GameRanking from '../components/GameRanking'
import { saveScore, loadGlobalRanking, loadPersonalRanking } from '../utils/firestoreService'
import UserHeader from '../components/UserHeader'
import GameStats from '../components/GameStats'
import RankingModal from '../components/RankingModal'
import './Breakout.css'

const CANVAS_WIDTH = 600
const CANVAS_HEIGHT = 600
const PADDLE_WIDTH = 100
const PADDLE_HEIGHT = 15
const BALL_SIZE = 10
const PADDLE_SPEED = 10
const BALL_SPEED = 4
const BRICK_ROWS = 5
const BRICK_COLS = 8
const BRICK_WIDTH = 70
const BRICK_HEIGHT = 20
const BRICK_PADDING = 5
const BRICK_OFFSET_TOP = 50
const BRICK_OFFSET_LEFT = 10

const BRICK_COLORS = ['#f00000', '#f0a000', '#f0f000', '#00f000', '#00eaff']

export default function Breakout({ user, onBackToMenu }) {
  const canvasRef = useRef()
  const [score, setScore] = useState(0)
  const [lives, setLives] = useState(3)
  const [level, setLevel] = useState(1)
  const [gameOver, setGameOver] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [paddle, setPaddle] = useState({ x: CANVAS_WIDTH / 2 - PADDLE_WIDTH / 2 })
  const [ball, setBall] = useState({
    x: CANVAS_WIDTH / 2,
    y: CANVAS_HEIGHT - 40,
    dx: BALL_SPEED,
    dy: -BALL_SPEED
  })
  const [bricks, setBricks] = useState([])
  const [planes, setPlanes] = useState([])
  const [bombs, setBombs] = useState([])
  const [globalRanking, setGlobalRanking] = useState([])
  const [personalRanking, setPersonalRanking] = useState([])
  const [showRanking, setShowRanking] = useState(false)
  const [savingScore, setSavingScore] = useState(false)
  const [scoreSaved, setScoreSaved] = useState(false)
  const keysPressed = useRef({})
  const gameLoopRef = useRef()
  const planeSpawnTimer = useRef(0)

  function createBricks() {
    const newBricks = []
    for (let row = 0; row < BRICK_ROWS; row++) {
      for (let col = 0; col < BRICK_COLS; col++) {
        newBricks.push({
          x: col * (BRICK_WIDTH + BRICK_PADDING) + BRICK_OFFSET_LEFT,
          y: row * (BRICK_HEIGHT + BRICK_PADDING) + BRICK_OFFSET_TOP,
          status: 1,
          color: BRICK_COLORS[row]
        })
      }
    }
    return newBricks
  }

  useEffect(() => {
    setBricks(createBricks())
  }, [])

  // Previne scroll durante o jogo
  useEffect(() => {
    document.body.classList.add('game-active')
    
    return () => {
      document.body.classList.remove('game-active')
    }
  }, [])

  // Rankings functions
  const loadRankings = useCallback(async () => {
    try {
      const [global, personal] = await Promise.all([
        loadGlobalRanking('breakout'),
        loadPersonalRanking(user?.uid, 'breakout')
      ])
      setGlobalRanking(global)
      setPersonalRanking(personal)
    } catch (error) {
      console.error('Error loading rankings:', error)
    }
  }, [user])

  const saveGameScore = useCallback(async (finalScore) => {
    if (!user || finalScore === 0) {
      console.log('Not saving Breakout score:', { user: !!user, score: finalScore })
      return
    }
    
    try {
      setSavingScore(true)
      console.log('Saving Breakout score:', finalScore)
      await saveScore(user, finalScore, level, Math.floor(finalScore / 10), 'breakout')
      await loadRankings()
      setSavingScore(false)
      setScoreSaved(true)
      
      setTimeout(() => {
        setShowRanking(true)
      }, 1500)
    } catch (error) {
      console.error('Error saving score:', error)
      setSavingScore(false)
    }
  }, [user, level, loadRankings])

  // Spawn aviões periodicamente
  const spawnPlane = useCallback(() => {
    if (gameOver || isPaused) return
    
    planeSpawnTimer.current++
    
    // Spawn a cada 3 segundos aproximadamente (180 frames a 60fps)
    if (planeSpawnTimer.current >= 180) {
      const newPlane = {
        id: Date.now(),
        x: Math.random() > 0.5 ? -50 : CANVAS_WIDTH + 50,
        y: 30 + Math.random() * 50,
        speed: (Math.random() > 0.5 ? 1 : -1) * (2 + Math.random() * 2),
        bombTimer: 60 + Math.floor(Math.random() * 60)
      }
      
      setPlanes(prev => [...prev, newPlane])
      planeSpawnTimer.current = 0
    }
  }, [gameOver, isPaused])

  // Atualiza aviões
  const updatePlanes = useCallback(() => {
    if (gameOver || isPaused) return

    setPlanes(prev => {
      return prev
        .map(plane => {
          const newPlane = { 
            ...plane, 
            x: plane.x + plane.speed,
            bombTimer: plane.bombTimer - 1
          }
          
          // Solta bomba
          if (newPlane.bombTimer <= 0) {
            setBombs(bombs => [...bombs, {
              id: Date.now() + Math.random(),
              x: newPlane.x,
              y: newPlane.y + 10,
              speed: 3
            }])
            newPlane.bombTimer = 60 + Math.floor(Math.random() * 60)
          }
          
          return newPlane
        })
        .filter(plane => {
          // Remove aviões que saíram da tela
          return plane.x > -100 && plane.x < CANVAS_WIDTH + 100
        })
    })
  }, [gameOver, isPaused])

  // Atualiza bombas
  const updateBombs = useCallback(() => {
    if (gameOver || isPaused) return

    setBombs(prev => {
      let bombsToKeep = prev
        .map(bomb => ({
          ...bomb,
          y: bomb.y + bomb.speed
        }))
        .filter(bomb => bomb.y < CANVAS_HEIGHT)
      
      // Verifica colisão de bombas com paddle (perde vida)
      bombsToKeep = bombsToKeep.filter(bomb => {
        if (bomb.y + 4 >= CANVAS_HEIGHT - PADDLE_HEIGHT &&
            bomb.y <= CANVAS_HEIGHT &&
            bomb.x >= paddle.x &&
            bomb.x <= paddle.x + PADDLE_WIDTH) {
          
          setLives(lives => {
            const newLives = lives - 1
            if (newLives <= 0) {
              setGameOver(true)
              setScore(currentScore => {
                saveGameScore(currentScore)
                return currentScore
              })
            }
            return newLives
          })
          
          return false // Remove bomba
        }
        return true
      })
      
      // Verifica colisão de bombas com blocos
      bombsToKeep.forEach(bomb => {
        setBricks(currentBricks => {
          return currentBricks.map(brick => {
            if (!brick.destroyed &&
                bomb.x > brick.x &&
                bomb.x < brick.x + BRICK_WIDTH &&
                bomb.y > brick.y &&
                bomb.y < brick.y + BRICK_HEIGHT) {
              
              setScore(s => s + 10)
              return { ...brick, destroyed: true }
            }
            return brick
          })
        })
      })
      
      return bombsToKeep
    })
  }, [gameOver, isPaused, paddle, saveGameScore])

  const movePaddle = useCallback(() => {
    if (gameOver || isPaused) return

    setPaddle(prev => {
      let newX = prev.x
      if (keysPressed.current['ArrowLeft']) {
        newX = Math.max(0, prev.x - PADDLE_SPEED)
      }
      if (keysPressed.current['ArrowRight']) {
        newX = Math.min(CANVAS_WIDTH - PADDLE_WIDTH, prev.x + PADDLE_SPEED)
      }
      return { x: newX }
    })
  }, [gameOver, isPaused])

  const updateBall = useCallback(() => {
    if (gameOver || isPaused) return

    setBall(prev => {
      let newX = prev.x + prev.dx
      let newY = prev.y + prev.dy
      let newDx = prev.dx
      let newDy = prev.dy

      // Wall collision
      if (newX <= 0 || newX >= CANVAS_WIDTH - BALL_SIZE) {
        newDx = -newDx
      }
      if (newY <= 0) {
        newDy = -newDy
      }

      // Paddle collision
      if (
        newY + BALL_SIZE >= CANVAS_HEIGHT - PADDLE_HEIGHT &&
        newX + BALL_SIZE >= paddle.x &&
        newX <= paddle.x + PADDLE_WIDTH
      ) {
        newDy = -Math.abs(newDy)
        const hitPos = (newX - paddle.x) / PADDLE_WIDTH
        newDx = (hitPos - 0.5) * 8
      }

      // Brick collision
      setBricks(prevBricks => {
        return prevBricks.map(brick => {
          if (brick.status === 1) {
            if (
              newX + BALL_SIZE > brick.x &&
              newX < brick.x + BRICK_WIDTH &&
              newY + BALL_SIZE > brick.y &&
              newY < brick.y + BRICK_HEIGHT
            ) {
              newDy = -newDy
              setScore(s => s + 10)
              return { ...brick, status: 0 }
            }
          }
          return brick
        })
      })

      // Plane collision (destroi avião ao acertar com a bola)
      setPlanes(prevPlanes => {
        return prevPlanes.filter(plane => {
          const planeHit = (
            newX + BALL_SIZE > plane.x &&
            newX < plane.x + 40 &&
            newY + BALL_SIZE > plane.y &&
            newY < plane.y + 8
          )
          
          if (planeHit) {
            newDy = -newDy
            setScore(s => s + 50) // Bonus por destruir avião
            return false // Remove avião
          }
          return true
        })
      })

      // Ball out of bounds
      if (newY >= CANVAS_HEIGHT) {
        setLives(l => {
          const newLives = l - 1
          if (newLives <= 0) {
            setGameOver(true)
            setScore(currentScore => {
              saveGameScore(currentScore)
              return currentScore
            })
          }
          return newLives
        })
        return {
          x: CANVAS_WIDTH / 2,
          y: CANVAS_HEIGHT - 40,
          dx: BALL_SPEED,
          dy: -BALL_SPEED
        }
      }

      return { x: newX, y: newY, dx: newDx, dy: newDy }
    })
  }, [paddle.x, gameOver, isPaused, saveGameScore])

  useEffect(() => {
    const activeBricks = bricks.filter(b => b.status === 1).length
    if (activeBricks === 0 && bricks.length > 0 && !gameOver) {
      setLevel(l => l + 1)
      setBricks(createBricks())
      setBall({
        x: CANVAS_WIDTH / 2,
        y: CANVAS_HEIGHT - 40,
        dx: BALL_SPEED * (1 + level * 0.1),
        dy: -BALL_SPEED * (1 + level * 0.1)
      })
    }
  }, [bricks, gameOver, level])

  useEffect(() => {
    loadRankings()
  }, [loadRankings])

  useEffect(() => {
    if (showRanking && !gameOver) {
      setIsPaused(true)
    }
  }, [showRanking, gameOver])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'p' || e.key === 'P') {
        setIsPaused(prev => !prev)
        return
      }
      if (e.key === 'r' || e.key === 'R') {
        if (!gameOver && !showRanking) {
          setIsPaused(true)
        }
        setShowRanking(prev => !prev)
        return
      }
      keysPressed.current[e.key] = true
    }

    const handleKeyUp = (e) => {
      keysPressed.current[e.key] = false
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [gameOver, showRanking])

  useEffect(() => {
    if (gameOver || isPaused) return

    gameLoopRef.current = setInterval(() => {
      movePaddle()
      updateBall()
      spawnPlane()
      updatePlanes()
      updateBombs()
    }, 1000 / 60)

    return () => clearInterval(gameLoopRef.current)
  }, [movePaddle, updateBall, spawnPlane, updatePlanes, updateBombs, gameOver, isPaused])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#0a0a0a'
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

    // Draw bricks
    bricks.forEach(brick => {
      if (brick.status === 1) {
        ctx.fillStyle = brick.color
        ctx.shadowBlur = 10
        ctx.shadowColor = brick.color
        ctx.fillRect(brick.x, brick.y, BRICK_WIDTH, BRICK_HEIGHT)
      }
    })

    // Draw planes (aviões estilo Atari 8-bits)
    planes.forEach(plane => {
      ctx.shadowBlur = 15
      ctx.shadowColor = '#f0f000'
      
      // Corpo do avião
      ctx.fillStyle = '#f0f000'
      ctx.fillRect(plane.x, plane.y, 40, 8)
      
      // Asas
      ctx.fillRect(plane.x + 10, plane.y - 5, 20, 3)
      ctx.fillRect(plane.x + 10, plane.y + 10, 20, 3)
      
      // Cockpit
      ctx.fillStyle = '#00eaff'
      ctx.fillRect(plane.x + 15, plane.y + 2, 10, 4)
      
      // Cauda
      ctx.fillStyle = '#f0f000'
      ctx.fillRect(plane.x - 5, plane.y + 2, 5, 4)
    })

    // Draw bombs
    bombs.forEach(bomb => {
      ctx.fillStyle = '#ff0000'
      ctx.shadowBlur = 10
      ctx.shadowColor = '#ff0000'
      ctx.beginPath()
      ctx.arc(bomb.x, bomb.y, 4, 0, Math.PI * 2)
      ctx.fill()
      
      // Trilha da bomba
      ctx.fillStyle = 'rgba(255, 0, 0, 0.3)'
      ctx.fillRect(bomb.x - 1, bomb.y - 10, 2, 10)
    })

    // Draw paddle
    ctx.fillStyle = '#00eaff'
    ctx.shadowBlur = 20
    ctx.shadowColor = '#00eaff'
    ctx.fillRect(paddle.x, CANVAS_HEIGHT - PADDLE_HEIGHT, PADDLE_WIDTH, PADDLE_HEIGHT)

    // Draw ball
    ctx.fillStyle = '#f0f000'
    ctx.shadowColor = '#f0f000'
    ctx.beginPath()
    ctx.arc(ball.x + BALL_SIZE / 2, ball.y + BALL_SIZE / 2, BALL_SIZE / 2, 0, Math.PI * 2)
    ctx.fill()

    ctx.shadowBlur = 0
  }, [paddle, ball, bricks, planes, bombs])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    let touchStartX = 0
    let touchMoving = false

    const handleTouchStart = (e) => {
      if (gameOver || isPaused) return
      const touch = e.touches[0]
      touchStartX = touch.clientX
      touchMoving = true
    }

    const handleTouchMove = (e) => {
      if (gameOver || isPaused || !touchMoving) return
      e.preventDefault()
      const touch = e.touches[0]
      const rect = canvas.getBoundingClientRect()
      const touchX = touch.clientX - rect.left
      const paddleX = Math.max(0, Math.min(CANVAS_WIDTH - PADDLE_WIDTH, touchX - PADDLE_WIDTH / 2))
      setPaddle({ x: paddleX })
    }

    const handleTouchEnd = (e) => {
      touchMoving = false
    }

    canvas.addEventListener('touchstart', handleTouchStart)
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false })
    canvas.addEventListener('touchend', handleTouchEnd)

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart)
      canvas.removeEventListener('touchmove', handleTouchMove)
      canvas.removeEventListener('touchend', handleTouchEnd)
    }
  }, [gameOver, isPaused])

  function resetGame() {
    setScore(0)
    setLives(3)
    setLevel(1)
    setGameOver(false)
    setIsPaused(false)
    setPaddle({ x: CANVAS_WIDTH / 2 - PADDLE_WIDTH / 2 })
    setBall({
      x: CANVAS_WIDTH / 2,
      y: CANVAS_HEIGHT - 40,
      dx: BALL_SPEED,
      dy: -BALL_SPEED
    })
    setBricks(createBricks())
    setPlanes([])
    setBombs([])
    planeSpawnTimer.current = 0
    setShowRanking(false)
    setSavingScore(false)
    setScoreSaved(false)
  }

  return (
    <div className="breakout-container">
      <UserHeader user={user} gameName="BREAKOUT" />

      <div className="game-layout">
        <GameStats 
          score={score} 
          lines={lives} 
          level={level} 
        />

        <div className="game-area">
          <canvas 
            ref={canvasRef} 
            width={CANVAS_WIDTH} 
            height={CANVAS_HEIGHT}
            className="game-canvas"
          />
          
          {/* Info Mobile Overlay */}
          <div className="mobile-info-overlay">
            <span>🏆 {score}</span>
            <span>❤️ {lives}</span>
            <span>📊 L{level}</span>
          </div>
          
          {gameOver && (
            <div className="game-overlay">
              <div className="overlay-content">
                <h2>GAME OVER</h2>
                <p className="final-score">Pontuação Final: <strong>{score}</strong></p>
                <p className="final-stats">Nível {level} • {Math.floor(score / 10)} blocos destruídos</p>
                
                {savingScore && (
                  <p className="saving-message">💾 Salvando pontuação...</p>
                )}
                
                {scoreSaved && (
                  <p className="saved-message">✅ Pontuação salva com sucesso!</p>
                )}
                
                <div className="game-over-buttons">
                  <button className="btn-primary" onClick={resetGame}>
                    🎮 Jogar Novamente
                  </button>
                  <button className="btn-secondary" onClick={() => setShowRanking(true)}>
                    🏆 Ver Rankings
                  </button>
                  {onBackToMenu && (
                    <button className="btn-secondary" onClick={onBackToMenu}>
                      ← Voltar ao Menu
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {isPaused && !gameOver && (
            <div className="game-overlay">
              <div className="overlay-content">
                <h2>PAUSADO</h2>
                <p>Pressione P para continuar</p>
              </div>
            </div>
          )}
        </div>

        <div className="sidebar right-sidebar">
          <div className="controls-panel">
            <h3>CONTROLES</h3>
            <div className="control-item">← → Mover Raquete</div>
            <div className="control-item">P Pausar</div>
            <div className="control-item">R Ranking</div>
          </div>
          
          <div className="info-panel">
            <h3>STATUS</h3>
            <div className="control-item">❤️ Vidas: {lives}</div>
            <div className="control-item">🎯 Blocos: {bricks.filter(b => b.status === 1).length}</div>
          </div>
          
          {!gameOver && (
            <button className="btn-secondary" onClick={() => setIsPaused(!isPaused)}>
              {isPaused ? 'Continuar' : 'Pausar'}
            </button>
          )}
          
          <button className="btn-secondary" onClick={resetGame}>
            Reiniciar
          </button>
          
          {onBackToMenu && (
            <button className="btn-back-menu" onClick={onBackToMenu}>
              ← Menu
            </button>
          )}
          
          <button className="btn-secondary" onClick={() => {
            if (!gameOver && !showRanking) setIsPaused(true)
            setShowRanking(!showRanking)
          }}>
            {showRanking ? 'Fechar Ranking' : 'Ver Ranking'}
          </button>
        </div>
      </div>

      <RankingModal
        show={showRanking}
        onClose={() => setShowRanking(false)}
        globalRanking={globalRanking}
        personalRanking={personalRanking}
        currentUserId={user?.uid}
        gameOver={gameOver}
      />


    </div>
  )
}
